import { lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { isPlatformHost } from '@/lib/platformHost';
import { getLegacyCredentialRedirect } from '@/lib/legacyRedirect';
import { AuthProvider, useAuth } from '../providers/AuthProvider';
import { SiteProvider } from '../providers/SiteProvider';
import { LoginScreen } from '@/features/auth/components/LoginScreen';
import { SignupScreen } from '@/features/auth/components/SignupScreen';
import { AuthCallback } from '@/features/auth/components/AuthCallback';
import { PublicSitePage } from '@/features/public-site/components/PublicSitePage';
import { PublicPageView } from '@/features/public-site/components/PublicPageView';
import { LegalLayout } from '@/pages/legal/LegalLayout';
import { NewsletterStatusPage } from '@/pages/newsletter/NewsletterStatusPage';

// O painel administrativo é carregado por rota. O site público não precisa
// baixar editores, mídia, newsletter e configurações antes da primeira pintura.
const DashboardLayout = lazy(() => import('@/features/dashboard/components/DashboardLayout').then(({ DashboardLayout }) => ({ default: DashboardLayout })));
const DashboardHome = lazy(() => import('@/features/dashboard/components/DashboardHome').then(({ DashboardHome }) => ({ default: DashboardHome })));
const NetworkDashboard = lazy(() => import('@/features/dashboard/components/NetworkDashboard').then(({ NetworkDashboard }) => ({ default: NetworkDashboard })));
const NetworkAISettings = lazy(() => import('@/features/network/components/NetworkAISettings').then(({ NetworkAISettings }) => ({ default: NetworkAISettings })));
const NetworkLegalSettings = lazy(() => import('@/features/legal/components/LegalSettings').then(({ NetworkLegalSettings }) => ({ default: NetworkLegalSettings })));
const SiteLegalSettings = lazy(() => import('@/features/legal/components/LegalSettings').then(({ SiteLegalSettings }) => ({ default: SiteLegalSettings })));
const ProfileLayout = lazy(() => import('@/features/profile/components/ProfileLayout').then(({ ProfileLayout }) => ({ default: ProfileLayout })));
const ProfileAccount = lazy(() => import('@/features/profile/components/ProfileAccount').then(({ ProfileAccount }) => ({ default: ProfileAccount })));
const ProfilePreferences = lazy(() => import('@/features/profile/components/ProfilePreferences').then(({ ProfilePreferences }) => ({ default: ProfilePreferences })));
const ProfilePrivacy = lazy(() => import('@/features/profile/components/ProfilePrivacy').then(({ ProfilePrivacy }) => ({ default: ProfilePrivacy })));
const SitesList = lazy(() => import('@/features/sites/components/SitesList').then(({ SitesList }) => ({ default: SitesList })));
const SiteDetail = lazy(() => import('@/features/sites/components/SiteDetail').then(({ SiteDetail }) => ({ default: SiteDetail })));
const PostsList = lazy(() => import('@/features/content/posts/components/PostsList').then(({ PostsList }) => ({ default: PostsList })));
const PostEditorPage = lazy(() => import('@/features/content/posts/components/PostEditorPage').then(({ PostEditorPage }) => ({ default: PostEditorPage })));
const PostPreviewPage = lazy(() => import('@/features/content/posts/components/PostPreviewPage').then(({ PostPreviewPage }) => ({ default: PostPreviewPage })));
const MediaLibrary = lazy(() => import('@/features/media/components/MediaLibrary').then(({ MediaLibrary }) => ({ default: MediaLibrary })));
const TaxonomiesPage = lazy(() => import('@/features/content/taxonomies/components/TaxonomiesPage').then(({ TaxonomiesPage }) => ({ default: TaxonomiesPage })));
const ThemeSettings = lazy(() => import('@/features/themes/components/ThemeSettings').then(({ ThemeSettings }) => ({ default: ThemeSettings })));
const AppearanceTemplateEditorPage = lazy(() => import('@/features/themes/components/AppearanceTemplateEditorPage').then(({ AppearanceTemplateEditorPage }) => ({ default: AppearanceTemplateEditorPage })));
const SEOSettings = lazy(() => import('@/features/seo/components/SEOSettings').then(({ SEOSettings }) => ({ default: SEOSettings })));
const AnalyticsSettings = lazy(() => import('@/features/analytics/components/AnalyticsSettings').then(({ AnalyticsSettings }) => ({ default: AnalyticsSettings })));
const NewsletterLayout = lazy(() => import('@/features/newsletter/components/NewsletterLayout').then(({ NewsletterLayout }) => ({ default: NewsletterLayout })));
const CampaignsList = lazy(() => import('@/features/newsletter/components/CampaignsList').then(({ CampaignsList }) => ({ default: CampaignsList })));
const CampaignEditor = lazy(() => import('@/features/newsletter/components/CampaignEditor').then(({ CampaignEditor }) => ({ default: CampaignEditor })));
const SubscribersList = lazy(() => import('@/features/newsletter/components/SubscribersList').then(({ SubscribersList }) => ({ default: SubscribersList })));
const EmailDomainSettings = lazy(() => import('@/features/newsletter/components/EmailDomainSettings').then(({ EmailDomainSettings }) => ({ default: EmailDomainSettings })));
const NewsletterChromeSettings = lazy(() => import('@/features/newsletter/components/NewsletterChromeSettings').then(({ NewsletterChromeSettings }) => ({ default: NewsletterChromeSettings })));
const SettingsLayout = lazy(() => import('@/features/settings/components/SettingsLayout').then(({ SettingsLayout }) => ({ default: SettingsLayout })));
const SkillsList = lazy(() => import('@/features/settings/components/SkillsList').then(({ SkillsList }) => ({ default: SkillsList })));
const AiWritingGuidelinesSettings = lazy(() => import('@/features/settings/components/AiWritingGuidelinesSettings').then(({ AiWritingGuidelinesSettings }) => ({ default: AiWritingGuidelinesSettings })));

function AppLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function PrivateRoute() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-surface text-text">
        <div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;

  return (
    <SiteProvider>
      <Outlet />
    </SiteProvider>
  );
}

/**
 * Em domínio custom de cliente (Cloudflare for SaaS), a raiz serve o site
 * público do tenant (PublicSitePage resolve via resolve_site_by_hostname);
 * nos hosts da plataforma, mantém o redirect para o painel admin.
 */
function RootDispatch() {
  return isPlatformHost() ? <Navigate to="/app" replace /> : <PublicSitePage />;
}

function PublicEntry() {
  const legacyRedirectUrl = getLegacyCredentialRedirect();

  useEffect(() => {
    if (legacyRedirectUrl) {
      window.location.replace(legacyRedirectUrl);
    }
  }, [legacyRedirectUrl]);

  if (legacyRedirectUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  return <PublicSitePage />;
}

function PublicOnlyRoute() {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (session) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function SuperadminRoute() {
  const { isSuperAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isSuperAdmin) return <Navigate to="/app" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      {
        element: <PublicOnlyRoute />,
        children: [
          { path: '/login', element: <LoginScreen /> },
          { path: '/signup', element: <SignupScreen /> },
        ],
      },
      { path: '/auth/callback', element: <AuthCallback /> },
      { path: '/legal/:documentKey', element: <LegalLayout /> },
      { path: '/legal', element: <Navigate to="/legal/privacidade" replace /> },
      { path: '/newsletter/:status', element: <NewsletterStatusPage /> },
      { path: '/', element: <RootDispatch /> },
      {
        path: '/s/:slug',
        element: <PublicSitePage />,
      },
      { path: '/s/:slug/legal/:documentKey', element: <LegalLayout /> },
      { path: '/s/:slug/legal', element: <Navigate to="privacidade" replace /> },
      { path: '/s/:slug/newsletter/:status', element: <NewsletterStatusPage /> },
      {
        path: '/s/:slug/:pageSlug',
        element: <PublicPageView />,
      },
      // Domínio custom do cliente: posts/páginas usam URL limpa, sem o
      // prefixo /s/:slug (é o que o Worker de roteamento já assume ao montar
      // og:url/canonical). PublicPageView resolve o site por hostname quando
      // não há :slug na rota — mesmo padrão de PublicSitePage/RootDispatch.
      // Fica depois de /s/:slug/:pageSlug (mais específica, sempre vence) e
      // antes do catch-all; só intercepta paths de 1 segmento, então não
      // colide com nenhuma rota interna do app (todas com path estático ou
      // 2+ segmentos).
      {
        path: '/:pageSlug',
        element: <PublicPageView />,
      },
      {
        element: <PrivateRoute />,
        children: [
          { path: '/posts/:id/preview', element: <PostPreviewPage /> },
          {
            element: <DashboardLayout />,
            children: [
              { path: '/app', element: <DashboardHome /> },
              {
                path: '/perfil',
                element: <ProfileLayout />,
                children: [
                  { path: '', element: <Navigate to="conta" replace /> },
                  { path: 'conta', element: <ProfileAccount /> },
                  { path: 'preferencias', element: <ProfilePreferences /> },
                  { path: 'privacidade', element: <ProfilePrivacy /> },
                ],
              },
              {
                path: '/rede',
                element: <SuperadminRoute />,
                children: [
                  { path: '', element: <NetworkDashboard /> },
                  { path: 'sites', element: <SitesList /> },
                  { path: 'sites/:id', element: <SiteDetail /> },
                  { path: 'configuracoes', element: <NetworkAISettings /> },
                  { path: 'documentos-legais', element: <NetworkLegalSettings /> },
                ],
              },
              { path: '/posts', element: <PostsList /> },
              { path: '/posts/novo', element: <PostEditorPage /> },
              { path: '/posts/:id', element: <PostEditorPage /> },
              { path: '/pages', element: <PostsList type="page" /> },
              { path: '/midias', element: <MediaLibrary /> },
              { path: '/categorias', element: <TaxonomiesPage type="category" /> },
              { path: '/tags', element: <TaxonomiesPage type="tag" /> },
              {
                path: '/newsletter',
                element: <NewsletterLayout />,
                children: [
                  { path: '', element: <CampaignsList /> },
                  { path: 'assinantes', element: <SubscribersList /> },
                  { path: 'modelo', element: <NewsletterChromeSettings /> },
                  { path: 'dominio', element: <EmailDomainSettings /> },
                ],
              },
              // Sem rota /novo: "Novo digest"/"Nova campanha" em CampaignsList.tsx já criam a linha e navegam direto pro :id — diferente de /posts/novo, uma campanha em rascunho não tem custo de ficar órfã.
              { path: '/newsletter/campanhas/:id', element: <CampaignEditor /> },
              { path: '/aparencia', element: <ThemeSettings /> },
              { path: '/aparencia/modelos/novo', element: <AppearanceTemplateEditorPage /> },
              { path: '/aparencia/modelos/:id', element: <AppearanceTemplateEditorPage /> },
              { path: '/analytics', element: <AnalyticsSettings /> },
              { path: '/seo', element: <SEOSettings /> },
              { path: '/documentos-legais', element: <SiteLegalSettings /> },
              {
                path: '/configuracoes',
                element: <SettingsLayout />,
                children: [
                  { path: '', element: <Navigate to="skills" replace /> },
                  { path: 'skills', element: <SkillsList /> },
                  { path: 'diretrizes-ia', element: <AiWritingGuidelinesSettings /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <PublicEntry />,
      },
    ],
  },
]);
